import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Combobox } from '@/components/ui/combobox';
import { DatePickerItalian } from '@/components/ui/date-picker-italian';
import { apiClient } from '@/utils/api';
import { formatItalianDateInput, parseItalianDateInput } from '@/utils/date';
import {
  BillingDocument,
  BillingLine,
  BillingDocType,
  PriceListUnit,
  PriceSuggestion,
  ComboboxClient,
  STATUS_LABEL,
  TYPE_LABEL,
  UNIT_LABEL,
  billingApi,
  clientDisplayName,
  euro,
} from '@/services/billingApi';
import { BillingSendDialog } from './BillingSendDialog';

const emptyLine = (): BillingLine => ({
  description: '',
  unit: 'CORPO',
  quantity: 1,
  unitPrice: 0,
});

function toIsoDate(italian: string): string | null {
  const d = parseItalianDateInput(italian);
  if (!d) return null;
  return dayjs(d).format('YYYY-MM-DD');
}

function fromIsoDate(iso?: string | null): string {
  if (!iso) return '';
  return formatItalianDateInput(iso);
}

export default function BillingDocumentPage() {
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'nuovo';
  const [doc, setDoc] = useState<BillingDocument | null>(null);
  const [type, setType] = useState<BillingDocType>((params.get('type') as BillingDocType) || 'QUOTE');
  const [clientId, setClientId] = useState(params.get('clientId') || '');
  const [practiceId, setPracticeId] = useState(params.get('practiceId') || '');
  const [clients, setClients] = useState<ComboboxClient[]>([]);
  const [practices, setPractices] = useState<Array<{ id: string; title: string; typeId?: string; typeName?: string }>>([]);
  const [lines, setLines] = useState<BillingLine[]>([emptyLine()]);
  const [notesPublic, setNotesPublic] = useState('');
  const [notesInternal, setNotesInternal] = useState('');
  const [suggestions, setSuggestions] = useState<PriceSuggestion[]>([]);
  const [overrideSeq, setOverrideSeq] = useState('');
  const [sendOpen, setSendOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validUntil, setValidUntil] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [openQuotes, setOpenQuotes] = useState<
    Array<{ id: string; numberDisplay: string | null; lines: Array<{ id: string; description: string; remainingQty: number; unitPrice: number; unit: PriceListUnit }> }>
  >([]);
  const [pickedQuotes, setPickedQuotes] = useState<string[]>([]);

  const isDraft = isNew || doc?.status === 'DRAFT';
  const savedDoc = isNew ? null : doc;
  const urlType = params.get('type');
  const urlClient = params.get('clientId');
  const urlPractice = params.get('practiceId');

  useEffect(() => {
    if (!isNew) return;
    setDoc(null);
    if (urlType === 'INVOICE' || urlType === 'CREDIT_NOTE' || urlType === 'QUOTE') {
      setType(urlType);
    }
    if (urlClient) setClientId(urlClient);
    if (urlPractice) setPracticeId(urlPractice);
  }, [isNew, urlType, urlClient, urlPractice]);

  useEffect(() => {
    apiClient
      .get('/clients/combobox')
      .then((r) => {
        const list = (r.data.data?.clients || r.data.data || []) as ComboboxClient[];
        setClients(Array.isArray(list) ? list : []);
      })
      .catch(() => undefined);
    if (isNew) {
      billingApi.settings().then((s: any) => {
        const days = type === 'QUOTE' ? Number(s.quoteValidityDays || 30) : Number(s.invoiceDueDays || 30);
        const italian = formatItalianDateInput(dayjs().add(days, 'day').toDate());
        if (type === 'QUOTE') setValidUntil(italian);
        else setDueDate(italian);
      }).catch(() => undefined);
    }
  }, [isNew, type]);

  useEffect(() => {
    if (!clientId) {
      setPractices([]);
      setOpenQuotes([]);
      return;
    }
    apiClient
      .get(`/practices`, { params: { clientId, limit: 100 } })
      .then((r) => {
        const list = r.data.data?.practices || [];
        setPractices(
          list.map((p: any) => ({
            id: p.id,
            title: `${p.practiceNumber ? `#${p.practiceNumber} ` : ''}${p.title}`,
            typeId: p.typeId || p.type?.id,
            typeName: p.type?.name,
          }))
        );
      })
      .catch(() => setPractices([]));
  }, [clientId]);

  const selectedPractice = practices.find((p) => p.id === practiceId);
  const practiceTypeId = selectedPractice?.typeId || (doc?.practice as { typeId?: string } | null | undefined)?.typeId;
  const practiceTypeName = selectedPractice?.typeName || (doc?.practice as { type?: { name?: string } } | null | undefined)?.type?.name;

  useEffect(() => {
    billingApi
      .suggest(clientId || undefined, undefined, practiceTypeId)
      .then(setSuggestions)
      .catch(() => undefined);
  }, [clientId, practiceTypeId]);

  useEffect(() => {
    if (!isDraft || type !== 'INVOICE' || !clientId) {
      setOpenQuotes([]);
      return;
    }
    billingApi
      .list({ type: 'QUOTE', clientId })
      .then(async (rows) => {
        const ids = rows
          .filter((q) => ['ISSUED', 'SENT', 'ACCEPTED', 'EXPIRED'].includes(q.status))
          .map((q) => q.id);
        if (!ids.length) {
          setOpenQuotes([]);
          return;
        }
        const remaining = await billingApi.remaining(ids);
        setOpenQuotes(
          (remaining as any[]).filter((q) => q.lines.some((l: any) => l.remainingQty > 0))
        );
      })
      .catch(() => setOpenQuotes([]));
  }, [isDraft, type, clientId]);

  useEffect(() => {
    if (isNew) return;
    billingApi
      .get(id!)
      .then((d) => {
        setDoc(d);
        setType(d.type);
        setClientId(d.clientId);
        setPracticeId(d.practiceId || '');
        setLines(d.lines.length ? d.lines : [emptyLine()]);
        setNotesPublic(d.notesPublic || '');
        setNotesInternal(d.notesInternal || '');
        setValidUntil(fromIsoDate(d.validUntil));
        setDueDate(fromIsoDate(d.dueDate));
      })
      .catch((e) => {
        toast.error(e.response?.data?.message || 'Documento non trovato');
        navigate('/preventivi-e-conti');
      });
  }, [id, isNew, navigate]);

  useEffect(() => {
    const fromQuote = params.get('fromQuote');
    const fromInvoice = params.get('fromInvoice');
    if (!isNew || (!fromQuote && !fromInvoice)) return;
    (async () => {
      try {
        if (fromQuote) {
          const remaining = await billingApi.remaining([fromQuote]);
          const q = remaining[0];
          if (!q) return;
          setDoc(null);
          setType('INVOICE');
          setClientId(q.clientId);
          setPracticeId(q.practiceId || '');
          setPickedQuotes([fromQuote]);
          setLines(
            q.lines
              .filter((l: any) => l.remainingQty > 0)
              .map((l: any) => ({
                description: l.description,
                unit: l.unit,
                quantity: l.remainingQty,
                unitPrice: l.unitPrice,
                sourceQuoteLineId: l.id,
              }))
          );
        }
        if (fromInvoice) {
          const inv = await billingApi.get(fromInvoice);
          setDoc(null);
          setType('CREDIT_NOTE');
          setClientId(inv.clientId);
          setPracticeId(inv.practiceId || '');
          setLines(
            inv.lines.map((l) => ({
              description: l.description,
              unit: l.unit,
              quantity: l.quantity,
              unitPrice: l.unitPrice,
              sourceQuoteLineId: l.id,
            }))
          );
        }
      } catch {
        /* ignore */
      }
    })();
  }, [isNew, params]);

  const total = useMemo(
    () => lines.reduce((s, l) => s + Number(l.quantity || 0) * Number(l.unitPrice || 0), 0),
    [lines]
  );

  const clientOptions = useMemo(
    () =>
      clients.map((c) => ({
        value: c.id,
        label: clientDisplayName(c),
        description: c.email || undefined,
      })),
    [clients]
  );

  const payload = () => ({
    type,
    clientId,
    practiceId: practiceId || null,
    notesPublic,
    notesInternal,
    validUntil: type === 'QUOTE' ? toIsoDate(validUntil) : null,
    dueDate: type !== 'QUOTE' ? toIsoDate(dueDate) : null,
    quoteIds: pickedQuotes.length ? pickedQuotes : params.get('fromQuote') ? [params.get('fromQuote') as string] : undefined,
    creditNoteOfId: params.get('fromInvoice') || undefined,
    lines: lines
      .filter((l) => l.description.trim())
      .map((l) => ({
        description: l.description,
        unit: l.unit,
        quantity: Number(l.quantity),
        unitPrice: Number(l.unitPrice),
        priceListItemId: l.priceListItemId || null,
        sourceQuoteLineId: l.sourceQuoteLineId || null,
      })),
  });

  const save = async () => {
    if (!clientId) {
      toast.error('Seleziona un cliente');
      return null;
    }
    if (!payload().lines.length) {
      toast.error('Aggiungi almeno una riga con descrizione');
      return null;
    }
    setSaving(true);
    try {
      const data = isNew ? await billingApi.create(payload()) : await billingApi.update(id!, payload());
      setDoc(data);
      if (isNew) navigate(`/preventivi-e-conti/${data.id}`, { replace: true });
      toast.success('Salvato');
      return data;
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Errore salvataggio');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const emit = async () => {
    let current = doc;
    if (isDraft) {
      current = (await save()) || current;
    }
    if (!current) return;
    try {
      const seq = overrideSeq ? Number(overrideSeq) : undefined;
      const data = await billingApi.emit(current.id, seq);
      setDoc(data);
      toast.success(`Emesso ${data.numberDisplay}`);
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Errore emissione');
    }
  };

  const applySuggestion = (idx: number, hit: PriceSuggestion) => {
    const next = [...lines];
    next[idx] = {
      ...next[idx],
      description: hit.description,
      unit: hit.unit,
      unitPrice: hit.unitPrice,
      priceListItemId: hit.id,
    };
    setLines(next);
  };

  const importQuotes = () => {
    const chosen = openQuotes.filter((q) => pickedQuotes.includes(q.id));
    const imported: BillingLine[] = chosen.flatMap((q) =>
      q.lines
        .filter((l) => l.remainingQty > 0)
        .map((l) => ({
          description: l.description,
          unit: l.unit,
          quantity: l.remainingQty,
          unitPrice: l.unitPrice,
          sourceQuoteLineId: l.id,
        }))
    );
    if (!imported.length) {
      toast.error('Nessuna riga residua nei preventivi selezionati');
      return;
    }
    setLines((prev) => {
      const existing = prev.filter((l) => l.description.trim());
      return existing.length ? [...existing, ...imported] : imported;
    });
    toast.success(`Importate ${imported.length} righe da preventivo`);
  };

  return (
    <div className="space-y-4 max-w-5xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isNew
              ? type === 'INVOICE'
                ? 'Nuova fattura'
                : type === 'CREDIT_NOTE'
                  ? 'Nuova nota di credito'
                  : 'Nuovo preventivo'
              : `${TYPE_LABEL[type]} ${doc?.numberDisplay || 'bozza'}`}
          </h1>
          {savedDoc && <Badge className="mt-1">{STATUS_LABEL[savedDoc.status]}</Badge>}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => navigate('/preventivi-e-conti')}>
            Elenco
          </Button>
          {savedDoc && (
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  await billingApi.openPdf(
                    savedDoc.id,
                    `${TYPE_LABEL[savedDoc.type]}-${savedDoc.numberDisplay || 'bozza'}.pdf`
                  );
                } catch (e: any) {
                  toast.error(e.response?.data?.message || e.message || 'Errore PDF');
                }
              }}
            >
              PDF
            </Button>
          )}
          {isDraft && (
            <Button variant="outline" onClick={save} disabled={saving}>
              Salva bozza
            </Button>
          )}
          {isDraft && (
            <Button onClick={emit} disabled={saving}>
              Emetti
            </Button>
          )}
          {savedDoc && savedDoc.status !== 'DRAFT' && (
            <Button onClick={() => setSendOpen(true)}>Invia</Button>
          )}
          {savedDoc && savedDoc.type === 'QUOTE' && savedDoc.status !== 'DRAFT' && (
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  const next = await billingApi.revision(savedDoc.id);
                  navigate(`/preventivi-e-conti/${next.id}`);
                } catch (e: any) {
                  toast.error(e.response?.data?.message || 'Revisione non possibile');
                }
              }}
            >
              Nuova revisione
            </Button>
          )}
          {savedDoc && savedDoc.type === 'QUOTE' && (savedDoc.status === 'ISSUED' || savedDoc.status === 'SENT') && (
            <>
              <Button variant="outline" onClick={() => billingApi.acceptance(savedDoc.id, 'ACCEPTED').then(setDoc)}>
                Accetta
              </Button>
              <Button variant="outline" onClick={() => billingApi.acceptance(savedDoc.id, 'REJECTED').then(setDoc)}>
                Rifiuta
              </Button>
              <Button variant="outline" onClick={() => navigate(`/preventivi-e-conti/nuovo?type=INVOICE&fromQuote=${savedDoc.id}`)}>
                Fattura da questo
              </Button>
            </>
          )}
          {savedDoc && savedDoc.type === 'INVOICE' && savedDoc.status !== 'DRAFT' && (
            <Button
              variant="outline"
              onClick={() => navigate(`/preventivi-e-conti/nuovo?type=CREDIT_NOTE&fromInvoice=${savedDoc.id}`)}
            >
              Nota di credito
            </Button>
          )}
          {isDraft && savedDoc && (
            <Button
              variant="destructive"
              onClick={async () => {
                if (!confirm('Cancellare la bozza?')) return;
                await billingApi.remove(savedDoc.id);
                navigate('/preventivi-e-conti');
              }}
            >
              Elimina
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Intestazione</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {isNew && (
            <div>
              <Label>Tipo</Label>
              <select
                className="w-full border rounded-md h-10 px-2"
                value={type}
                onChange={(e) => setType(e.target.value as BillingDocType)}
              >
                <option value="QUOTE">Preventivo</option>
                <option value="INVOICE">Fattura</option>
                <option value="CREDIT_NOTE">Nota di credito</option>
              </select>
            </div>
          )}
          <div>
            <Label>Cliente</Label>
            <Combobox
              options={clientOptions}
              value={clientId}
              onValueChange={(v) => {
                setClientId(v);
                setPracticeId('');
                setPickedQuotes([]);
              }}
              placeholder="Cerca cliente..."
              searchPlaceholder="Nome, cognome o ragione sociale"
              emptyMessage="Nessun cliente trovato"
              disabled={!isDraft}
            />
          </div>
          <div>
            <Label>Pratica (opzionale)</Label>
            <Combobox
              options={[
                { value: '', label: 'Nessuna' },
                ...practices.map((p) => ({ value: p.id, label: p.title })),
              ]}
              value={practiceId}
              onValueChange={setPracticeId}
              placeholder={clientId ? 'Cerca pratica...' : 'Prima scegli il cliente'}
              searchPlaceholder="Cerca pratica"
              emptyMessage="Nessuna pratica per questo cliente"
              disabled={!isDraft || !clientId}
            />
          </div>
          {type === 'QUOTE' && (
            <div>
              <Label>Validità preventivo</Label>
              {isDraft ? (
                <DatePickerItalian value={validUntil} onChange={setValidUntil} />
              ) : (
                <div className="h-10 flex items-center text-sm">{validUntil || '—'}</div>
              )}
            </div>
          )}
          {type !== 'QUOTE' && (
            <div>
              <Label>Scadenza pagamento</Label>
              {isDraft ? (
                <DatePickerItalian value={dueDate} onChange={setDueDate} />
              ) : (
                <div className="h-10 flex items-center text-sm">{dueDate || '—'}</div>
              )}
            </div>
          )}
          {isDraft && (
            <div>
              <Label>Override numero in emissione</Label>
              <Input
                inputMode="numeric"
                placeholder="vuoto = automatico"
                value={overrideSeq}
                onChange={(e) => setOverrideSeq(e.target.value.replace(/\D/g, ''))}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {isDraft && type === 'INVOICE' && openQuotes.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Da preventivi aperti</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-gray-500">
              Seleziona i preventivi con residuo da fatturare: le righe restanti vengono copiate in fattura.
            </p>
            {openQuotes.map((q) => (
              <label key={q.id} className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={pickedQuotes.includes(q.id)}
                  onChange={(e) => {
                    setPickedQuotes((prev) =>
                      e.target.checked ? [...prev, q.id] : prev.filter((x) => x !== q.id)
                    );
                  }}
                />
                <span>
                  <span className="font-medium">{q.numberDisplay || 'Preventivo'}</span>
                  <span className="text-gray-500">
                    {' '}
                    — {q.lines.filter((l) => l.remainingQty > 0).length} righe residue
                  </span>
                </span>
              </label>
            ))}
            <Button size="sm" variant="outline" onClick={importQuotes} disabled={!pickedQuotes.length}>
              Importa righe selezionate
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Righe</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {lines.map((line, idx) => (
            <div key={idx} className="rounded-md border p-3 space-y-2">
              <div>
                <Label>Descrizione / listino</Label>
                {practiceTypeName ? (
                  <p className="text-xs text-gray-500 mb-1">
                    Prima le voci per {practiceTypeName}, poi quelle globali (senza tipo).
                  </p>
                ) : (
                  <p className="text-xs text-gray-500 mb-1">
                    Seleziona una pratica per vedere il listino di quel tipo (es. SCIA).
                  </p>
                )}
                <Combobox
                  options={suggestions.map((s) => ({
                    value: s.id,
                    label: s.description,
                    description: [
                      euro(s.unitPrice),
                      UNIT_LABEL[s.unit],
                      s.scope === 'practice' || (s.practiceTypeNames && s.practiceTypeNames.length)
                        ? (s.practiceTypeNames || []).join(', ')
                        : 'globale',
                      s.fromClient ? 'ultimo prezzo cliente' : '',
                    ]
                      .filter(Boolean)
                      .join(' · '),
                  }))}
                  value={line.priceListItemId || ''}
                  displayLabel={line.description || undefined}
                  onValueChange={(itemId) => {
                    const hit = suggestions.find((s) => s.id === itemId);
                    if (hit) applySuggestion(idx, hit);
                  }}
                  onCustomValue={(text) => {
                    const next = [...lines];
                    next[idx] = { ...line, description: text, priceListItemId: null };
                    setLines(next);
                  }}
                  placeholder={practiceTypeName ? `Voci ${practiceTypeName} o scrivi…` : 'Cerca nel listino o scrivi una voce'}
                  searchPlaceholder="Cerca voce di listino..."
                  emptyMessage="Nessuna voce: scrivi e premi Invio"
                  disabled={!isDraft}
                />
              </div>
              <div className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-4 md:col-span-3">
                  <Label>Unità</Label>
                  <select
                    className="w-full border rounded-md h-10 px-2"
                    value={line.unit}
                    disabled={!isDraft}
                    onChange={(e) => {
                      const next = [...lines];
                      next[idx] = { ...line, unit: e.target.value as PriceListUnit };
                      setLines(next);
                    }}
                  >
                    {Object.entries(UNIT_LABEL).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-span-4 md:col-span-2">
                  <Label>Qtà</Label>
                  <Input
                    type="number"
                    step="0.001"
                    value={line.quantity}
                    disabled={!isDraft}
                    onChange={(e) => {
                      const next = [...lines];
                      next[idx] = { ...line, quantity: Number(e.target.value) };
                      setLines(next);
                    }}
                  />
                </div>
                <div className="col-span-4 md:col-span-3">
                  <Label>Prezzo</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={line.unitPrice}
                    disabled={!isDraft}
                    onChange={(e) => {
                      const next = [...lines];
                      next[idx] = { ...line, unitPrice: Number(e.target.value) };
                      setLines(next);
                    }}
                  />
                </div>
                <div className="col-span-6 md:col-span-2 text-sm font-medium pb-2">
                  {euro(Number(line.quantity || 0) * Number(line.unitPrice || 0))}
                  {line.remainingQty != null && (
                    <div className="text-xs text-gray-500 font-normal">residuo {line.remainingQty}</div>
                  )}
                </div>
                {isDraft && (
                  <div className="col-span-6 md:col-span-2 flex gap-1 pb-1 justify-end">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      title="Salva nel listino"
                      onClick={async () => {
                        if (!line.description.trim()) return;
                        try {
                          await billingApi.saveLineToList({
                            description: line.description,
                            unit: line.unit,
                            unitPrice: line.unitPrice,
                            practiceTypeIds: practiceTypeId ? [practiceTypeId] : [],
                          });
                          toast.success('Voce salvata nel listino');
                          billingApi.suggest(clientId || undefined, undefined, practiceTypeId).then(setSuggestions);
                        } catch (e: any) {
                          toast.error(e.response?.data?.message || 'Errore listino');
                        }
                      }}
                    >
                      In listino
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        setLines(
                          lines.filter((_, i) => i !== idx).length
                            ? lines.filter((_, i) => i !== idx)
                            : [emptyLine()]
                        )
                      }
                    >
                      ×
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
          {isDraft && (
            <Button variant="outline" size="sm" onClick={() => setLines([...lines, emptyLine()])}>
              Aggiungi riga
            </Button>
          )}
          <div className="text-right text-lg font-semibold">Totale {euro(total)}</div>
          {doc?.type === 'INVOICE' && doc.allocated != null && (
            <div className="text-right text-sm text-gray-600">
              Allocato {euro(doc.allocated)} · Credito NC {euro(doc.credited || 0)}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label>Note sul PDF</Label>
          <Textarea value={notesPublic} disabled={!isDraft} onChange={(e) => setNotesPublic(e.target.value)} rows={3} />
        </div>
        <div>
          <Label>Note interne</Label>
          <Textarea value={notesInternal} disabled={!isDraft} onChange={(e) => setNotesInternal(e.target.value)} rows={3} />
        </div>
      </div>

      {doc && sendOpen && (
        <BillingSendDialog
          document={doc}
          onClose={() => setSendOpen(false)}
          onSent={(d) => {
            setDoc(d);
            setSendOpen(false);
          }}
        />
      )}
    </div>
  );
}
